<template>
  <div class="menu_container">
    <div class="sidebar-brand">
      <div class="header-menu">
        <img
          :src="imageSrc"
          alt="Isologotipo App"
          style="height: 75%"
          @click="handleLogoClick"
        />
      </div>
      <span class="app-version">
        {{ version }}
      </span>

      <span
        v-if="mostrarNombreClienteSisPro"
        class="client-name"
        :title="nombreClienteSisPro"
      >
        {{ nombreClienteSisPro }}
      </span>
    </div>
    <el-menu
      :default-active="activeMenu"
      class="side-menu"
      :collapse="menuCollapsed"
      unique-opened
    >
      <template
        v-for="(item, index) in visibleMenuItems"
        :key="item.route || String(index)"
      >
        <el-sub-menu
          v-if="item.children"
          :index="String(index)"
        >
          <template #title>
            <el-icon>
              <component
                :is="item.icon"
                v-if="item.icon"
              />
            </el-icon>
            <span>{{ item.title }}</span>
          </template>

          <template
            v-for="(child, childIndex) in item.children"
            :key="child.route || childIndex"
          >
            <el-sub-menu
              v-if="child.children"
              :index="`${index}-${childIndex}`"
            >
              <template #title
                ><span>{{ child.title }}</span></template
              >

              <el-menu-item
                v-for="subChild in child.children"
                :key="subChild.route"
                :index="subChild.route"
                @click="handleMenuItemClick(subChild)"
              >
                {{ subChild.title }}
              </el-menu-item>
            </el-sub-menu>

            <el-menu-item
              v-else
              :index="child.route"
              @click="handleMenuItemClick(child)"
            >
              {{ child.title }}
            </el-menu-item>
          </template>
        </el-sub-menu>

        <el-menu-item
          v-else
          :index="item.route || String(index)"
          :disabled="item.disabled"
          :class="item.cssClass"
          @click="handleMenuItemClick(item)"
        >
          <el-icon>
            <component
              :is="item.icon"
              v-if="item.icon"
            />
          </el-icon>
          <template #title
            ><span>{{ item.title }}</span></template
          >
        </el-menu-item>
      </template>
    </el-menu>

    <el-button
      v-if="!isMobile"
      size="default"
      circle
      type="primary"
      :icon="arrowMenu"
      class="arrow_menu_container"
      @click="collapseMenu"
    ></el-button>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';
import {
  useRouter,
  useRoute,
} from 'vue-router';
import { useDeviceDetection } from '@/shared/composables/use_device_detection.js';
import Isotipo from '@/assets/images/isotipo.png';
import Isologotipo from '@/assets/images/isologotipo.png';
import IconLogout from '~icons/mdi/logout';
import IconChevronRight from '~icons/mdi/chevron-right';
import IconChevronLeft from '~icons/mdi/chevron-left';
import IconPeso from '~icons/mdi/dollar';
import IconGear from '~icons/mdi/gear-outline';
import IconReport from '~icons/mdi/report-box-plus-outline';
import IconDashboard from '~icons/mdi/view-dashboard';
import IconCogs from '~icons/mdi/cogs';
import IconMonitor from '~icons/mdi/monitor';
import IconPackageVariant from '~icons/mdi/package-variant';
import IconChartGantt from '~icons/mdi/chart-gantt';
import IconSetSquare from '~icons/mdi/set-square';
import IconProcess from '~icons/mdi/calendar-import-outline';
import IconSwapHorizontal from '~icons/mdi/swap-horizontal';
import { useAuthStore } from '@/views/auth/auth_store';
import { useRequestStore } from '@/views/requests/stores/request_store.js';
import { canViewModule } from '@/shared/permissions/permission.utils.js';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { useConfiguracionesPorDefectoStore } from '@/views/configuraciones_por_defecto/stores/configuraciones_por_defecto_store.js';
import { storeToRefs } from 'pinia';

const emit = defineEmits(['menu-item-click']);
const { screenWidth, isMobile } = useDeviceDetection();
const isCollapse = ref(true);
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const usuario = authStore.usuario();
const requestStore = useRequestStore();
const configuracionStore = useConfiguracionStore();
const configuracionesPorDefectoStore = useConfiguracionesPorDefectoStore();
const { mostrarIdentidadCliente, nombreClienteSisPro } = storeToRefs(configuracionStore);
const { resetFilter } = requestStore;
const version = __APP_VERSION__;

const isMobileMode = computed(() => screenWidth.value <= 768);
const menuCollapsed = computed(() => (isMobile.value ? false : isCollapse.value));
const activeMenu = computed(() => route.path);

const menuItems = [
  {
    title: 'Inicio',
    icon: IconDashboard,
    route: '/dashboard',
    showMobile: false,
  },
  {
    title: 'Cotizaciones',
    icon: IconPeso,
    showMobile: false,
    children: [
      {
        title: 'Solicitudes',
        route: '/page/requests/list',
        moduleKey: 'solicitud',
        showMobile: false,
      },
      {
        title: 'Cotizaciones',
        route: '/page/prices/list',
        moduleKey: 'cotizacion',
        showMobile: false,
      },
      {
        title: 'Propuestas',
        route: '/page/proposals/list',
        moduleKey: 'propuestas',
        showMobile: false,
      },
    ],
  },
  {
    title: 'Especificaciones Técnicas',
    icon: IconSetSquare,
    route: '/page/technical-specifications/list',
    moduleKey: 'especificacion-tecnica',
    showMobile: false,
  },
  {
    title: 'Ordenes de Producción',
    icon: IconCogs,
    route: '/page/work-orders/list',
    moduleKey: 'orden-produccion',
    showMobile: false,
  },
  {
    title: 'Planeamiento',
    icon: IconChartGantt,
    disabled: true,
    showMobile: false,
  },
  {
    title: 'Movimientos de Inventarios',
    icon: IconPackageVariant,
    moduleKey: 'stock',
    showMobile: false,
    children: [
      {
        title: 'Movimientos de Inventarios',
        route: '/page/inventory-movement/list',
        moduleKey: 'stock',
        showMobile: false,
      },
      {
        title: 'Toma Inventario',
        route: '/page/inventory/list',
        moduleKey: 'stock',
        showMobile: false,
      },
    ],
  },
  {
    // COTI-594: a diferencia de "Movimientos WIP" (pallet), este módulo tiene entrada de
    // menú propia. Se usa tanto en mobile (el operario desde el piso con una pistola) como en
    // desktop, por eso `showMobile: true`.
    title: 'Traslado de Stock',
    icon: IconSwapHorizontal,
    route: '/page/traslado-stock',
    moduleKey: 'traslado-stock',
    showMobile: true,
  },
  {
    title: 'Monitor',
    icon: IconMonitor,
    route: '/page/monitor/monitor-tv',
    moduleKey: 'monitor-tv',
    showMobile: true,
  },
  {
    title: 'Reportes',
    icon: IconReport,
    route: '/page/reports/list',
    moduleKey: 'reportes',
    showMobile: false,
  },
  {
    title: 'Configuraciones',
    icon: IconGear,
    showMobile: false,
    children: [
      {
        title: 'Maestros',
        children: [
          {
            title: 'Prospectos y Clientes',
            route: '/page/prospects/list',
            moduleKey: 'prospectos',
            showMobile: false,
          },
          {
            title: 'Proveedores',
            route: '/page/supplier/list',
            moduleKey: 'proveedor',
            showMobile: false,
          },
          { title: 'Agentes', route: '/page/agent/list', moduleKey: 'agente', showMobile: false },
          {
            title: 'Tipos de Tratado',
            route: '/page/tipo-tratado/list',
            moduleKey: 'tipo-tratado',
            showMobile: false,
          },
          {
            title: 'Colores',
            route: '/page/colores/list',
            moduleKey: 'colores',
            showMobile: false,
          },
          {
            title: 'Recursos',
            route: '/page/resource/list',
            moduleKey: 'recursos',
            showMobile: false,
          },
          {
            title: 'Cilindros',
            route: '/page/cylinders/list',
            moduleKey: 'cilindros',
            showMobile: false,
          },
          {
            title: 'Engrane Troquel',
            route: '/page/cylinder-adapters/list',
            moduleKey: 'cilindros',
            showMobile: false,
            configKey: 'mostrarGestionAdaptadores',
          },
          { title: 'Suajes', route: '/page/swage/list', moduleKey: 'suajes', showMobile: false },
          {
            title: 'Items',
            route: '/page/material/list',
            moduleKey: 'materiales',
            showMobile: false,
          },
          {
            title: 'Conceptos',
            route: '/page/setting/conceptos',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Depósitos',
            route: '/page/setting/depositos',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Talonarios',
            route: '/page/setting/talonarios',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Ubicaciones',
            route: '/page/setting/ubicaciones',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Monedas',
            route: '/page/setting/monedas',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Usuarios',
            route: '/page/users/list',
            moduleKey: 'usuarios',
            showMobile: false,
          },
          {
            title: 'Roles',
            route: '/page/setting/roles',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
        ],
      },
      {
        title: 'Parámetros',
        showMobile: false,
        children: [
          {
            title: 'Generales',
            route: '/page/setting/general',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Configuraciones por defecto',
            route: '/page/configuraciones-por-defecto/list',
            moduleKey: 'configuraciones-por-defecto',
            showMobile: false,
          },
          {
            title: 'Productos personalizados',
            route: '/page/setting/custom-product',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Grupos de elementos',
            route: '/page/setting/element-group',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'APIs',
            route: '/page/setting/apis',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Atributos Dinámicos',
            route: '/page/setting/attributes',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Generación archivos',
            route: '/page/setting/generate-files',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
          {
            title: 'Inventarios',
            route: '/page/setting/inventory',
            moduleKey: 'configuracion-general',
            showMobile: false,
          },
        ],
      },
    ],
  },
  {
    title: 'Procesos',
    icon: IconProcess,
    showMobile: false,
    children: [
      {
        title: 'Importación Clientes',
        route: '/page/setting/import-clients',
        moduleKey: 'configuracion-general',
        showMobile: false,
      },
      {
        title: 'Importación Items',
        route: '/page/setting/import-materials',
        moduleKey: 'configuracion-general',
        showMobile: false,
      },
      {
        title: 'Importación Suajes',
        route: '/page/setting/import-swages',
        moduleKey: 'configuracion-general',
        showMobile: false,
      },
    ],
  },
  {
    title: 'Cerrar sesión',
    icon: IconLogout,
    action: 'closeSession',
    cssClass: 'profile_menu',
    showMobile: true,
  },
];

const arrowMenu = computed(() => (isCollapse.value ? IconChevronRight : IconChevronLeft));
const imageSrc = computed(() => (isMobile.value || !isCollapse.value ? Isologotipo : Isotipo));

const visibleMenuItems = computed(() => {
  const filterItems = (items) => {
    return items.reduce((acc, item) => {
      if (item.disabled) {
        acc.push(item);
        return acc;
      }
      if (isMobile.value && !item.showMobile) return acc;

      const hasPermission = !item.moduleKey || (usuario && canViewModule(usuario, item.moduleKey));
      const hasConfigFlag = !item.configKey || configuracionStore[item.configKey];

      if (item.children) {
        const visibleChildren = filterItems(item.children);
        if (visibleChildren.length > 0) {
          acc.push({ ...item, children: visibleChildren });
        }
      } else if (hasPermission && hasConfigFlag) {
        acc.push(item);
      }
      return acc;
    }, []);
  };
  return filterItems(menuItems);
});

const handleMenuItemClick = (item) => {
  if (item.action === 'closeSession') {
    closeSession();
  } else if (item.route) {
    router.push(item.route);
    if (isMobile.value) emit('menu-item-click');
  }
};

const handleLogoClick = () => {
  router.push('/dashboard');
  if (isMobile.value) emit('menu-item-click');
};

const mostrarNombreClienteSisPro = computed(() => {
  return mostrarIdentidadCliente.value && nombreClienteSisPro.value;
});

watch(
  () => isMobile.value,
  (isMobileNow) => {
    if (isMobileNow) isCollapse.value = false;
  },
  { immediate: true },
);

const collapseMenu = () => {
  if (isMobile.value) {
    isCollapse.value = false;
    return;
  }
  isCollapse.value = !isCollapse.value;
};

const closeSession = async () => {
  resetFilter();
  await authStore.cerrarSesionMobile();
  router.replace('/');
};

onMounted(async () => {
  await configuracionStore.firstConfiguraciones();
  await configuracionesPorDefectoStore.findAllHTTP();
});
</script>

<style scoped>
.menu_container {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
}
.arrow_menu_container {
  position: absolute;
  top: 112px;
  right: -15px;
  z-index: 10;
}
.header-menu {
  height: 60px;
  border-bottom: 2px solid rgb(241, 241, 241);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: #409eff;
}
.side-menu {
  border: none;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  position: relative;
}

.side-menu.el-menu--collapse {
  width: 80px !important;
}

.configuration_menu {
  border-top: 1px solid rgb(241, 241, 241);
}

.profile_menu {
  position: relative;
  margin-top: auto;
}

.el-menu-item {
  height: 42px;
}

.side-menu {
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  overflow-x: hidden !important;
}

.side-menu:not(.el-menu--collapse) {
  width: 270px !important;
}
.side-menu.el-menu--collapse {
  width: 80px !important;
}
.side-menu :deep(span) {
  white-space: nowrap !important;
  overflow: hidden !important;
  display: inline-block !important;
  vertical-align: middle;
  transition:
    opacity 0.2s ease,
    width 0.2s ease !important;
}

.side-menu.el-menu--collapse :deep(.el-menu-item span),
.side-menu.el-menu--collapse :deep(.el-sub-menu__title span) {
  opacity: 0 !important;
  width: 0 !important;
  visibility: hidden !important;
}

.side-menu:not(.el-menu--collapse) :deep(.el-menu-item span),
.side-menu:not(.el-menu--collapse) :deep(.el-sub-menu__title span) {
  opacity: 1 !important;
  width: auto !important;
  visibility: visible !important;
  transition-delay: 0.1s !important;
}
.sidebar-brand {
  flex-shrink: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgb(241, 241, 241);
}

.app-version {
  text-align: center;
  font-size: 12px;
  display: block;
  padding: 5px 4px 2px;
}

.client-name {
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  display: block;
  padding: 0 24px 6px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
